import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { TextInput } from "react-native";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { HomeIcon, LucideIcon, MailIcon } from "lucide-react-native";
import { useForm } from "react-hook-form";

import VnAddressInput from "@/components/VnAddressInput";
import { VnBox } from "@/components/VnBox";
import { VnCheckboxInput } from "@/components/VnCheckboxInput/VnCheckboxInput";
import { VnExpandableCard } from "@/components/VnExpandableCard";
import { VnForm } from "@/components/VnForm";
import { VnIcon } from "@/components/VnIcon";
import { VnText } from "@/components/VnText/VnText";
import { VnTextInput } from "@/components/VnTextInput/VnTextInput";
import { useUpdateHouseholdMemberMutation } from "@/data/mutations/householdMembersMutations";
import { useCurrentHouseholdMemberQuery } from "@/data/queries/householdMembersQueries";
import {
  householdMemberAddressFormSchema,
  HouseholdMemberAddressFormType,
  HouseholdMemberType,
} from "@/domain/householdMember";
import { useTranslator } from "@/hooks/useTranslate";
import { I18nKeyType } from "@/i18n";
import { formatAddress, parseAddressComponents } from "@/utils/address";
import {
  formatHouseholdMemberName,
  setMailingAddressFormFields,
  setMainAddressFormFields,
} from "@/utils/householdMember";

type CardInfoPropsType = {
  icon: LucideIcon;
  labelI18nKey?: I18nKeyType;
  children?: React.ReactNode;
};

const CardInfo: FC<CardInfoPropsType> = ({ icon, children, labelI18nKey }) => {
  return (
    <VnBox className="flex flex-row items-center gap-2">
      <VnIcon as={icon} size={20} />
      <VnText variant="lg" i18nKey={labelI18nKey}>
        {children}
      </VnText>
    </VnBox>
  );
};

const AddressSummary = ({
  isHomeless,
  hasNoMailingAddress,
  formattedMainAddress,
  formattedMailingAddress,
  hasSameMainAddress,
  hasSameMailingAddress,
}: {
  isHomeless: boolean;
  hasNoMailingAddress: boolean;
  formattedMainAddress: string;
  formattedMailingAddress: string;
  hasSameMainAddress: boolean;
  hasSameMailingAddress: boolean;
}) => {
  const shouldDisplayNeedsHomeAddress = Boolean(
    !isHomeless && !formattedMainAddress,
  );
  const shouldDisplayNeedsMailingAddress = Boolean(
    !hasNoMailingAddress && !formattedMailingAddress,
  );
  const shouldDisplayMainAddress = Boolean(
    formattedMainAddress && !hasSameMainAddress,
  );
  const shouldDisplayMailingAddress = Boolean(
    formattedMailingAddress && !hasNoMailingAddress && !hasSameMailingAddress,
  );
  return (
    <VnBox className="gap-3">
      {isHomeless && (
        <CardInfo
          icon={HomeIcon}
          labelI18nKey="native:applicationAddressScreen.isHomeless"
        />
      )}
      {shouldDisplayNeedsHomeAddress && (
        <CardInfo
          icon={HomeIcon}
          labelI18nKey="native:applicationAddressScreen.needsHomeAddress"
        />
      )}
      {shouldDisplayMainAddress && (
        <CardInfo icon={HomeIcon}>{formattedMainAddress}</CardInfo>
      )}
      {hasSameMainAddress && (
        <CardInfo
          icon={HomeIcon}
          labelI18nKey="native:applicationAddressScreen.sameHomeAddress"
        />
      )}
      {hasNoMailingAddress && (
        <CardInfo
          icon={MailIcon}
          labelI18nKey="native:applicationAddressScreen.noMailingAddress"
        />
      )}
      {shouldDisplayMailingAddress && (
        <CardInfo icon={MailIcon}>{formattedMailingAddress}</CardInfo>
      )}
      {hasSameMailingAddress && (
        <CardInfo
          icon={MailIcon}
          labelI18nKey="native:applicationAddressScreen.sameMailingAddress"
        />
      )}
      {shouldDisplayNeedsMailingAddress && (
        <CardInfo
          icon={MailIcon}
          labelI18nKey="native:applicationAddressScreen.needsMailingAddress"
        />
      )}
    </VnBox>
  );
};

type HouseholdMemberAddressFormPropsType = {
  member: HouseholdMemberType;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onClose: () => void;
  onPrimaryApplicantSaveSuccess?: (params: {
    savedFormData: HouseholdMemberAddressFormType;
    oldMemberData: HouseholdMemberType;
    onDone: () => void;
  }) => void;
};

export const HouseholdMemberAddressForm: React.FC<
  HouseholdMemberAddressFormPropsType
> = ({
  member,
  isExpanded,
  onToggleExpand,
  onClose,
  onPrimaryApplicantSaveSuccess,
}) => {
  const { t } = useTranslator();
  const mainAddressRef = useRef<TextInput>(null);
  const mailingAddressRef = useRef<TextInput>(null);

  const { data: primaryApplicant } = useCurrentHouseholdMemberQuery();

  const isPrimaryApplicant = primaryApplicant?.id === member.id;

  const { mutate: updateHouseholdMember, isPending } =
    useUpdateHouseholdMemberMutation();

  const formattedMainAddress = useMemo(
    () =>
      formatAddress({
        streetOne: member.mainAddressStreetOne,
        streetTwo: member.mainAddressStreetTwo,
        city: member.mainAddressCity,
        state: member.mainAddressState,
        zip: member.mainAddressZip,
      }),
    [member],
  );

  const formattedMailingAddress = useMemo(
    () =>
      formatAddress({
        streetOne: member.mailingAddressStreetOne,
        streetTwo: member.mailingAddressStreetTwo,
        city: member.mailingAddressCity,
        state: member.mailingAddressState,
        zip: member.mailingAddressZip,
      }),
    [member],
  );

  const primaryApplicantFormattedAddress = useMemo(() => {
    const mainAddress = formatAddress({
      streetOne: primaryApplicant?.mainAddressStreetOne,
      streetTwo: primaryApplicant?.mainAddressStreetTwo,
      city: primaryApplicant?.mainAddressCity,
      state: primaryApplicant?.mainAddressState,
      zip: primaryApplicant?.mainAddressZip,
    });
    const mailingAddress = formatAddress({
      streetOne: primaryApplicant?.mailingAddressStreetOne,
      streetTwo: primaryApplicant?.mailingAddressStreetTwo,
      city: primaryApplicant?.mailingAddressCity,
      state: primaryApplicant?.mailingAddressState,
      zip: primaryApplicant?.mailingAddressZip,
    });
    return { mainAddress, mailingAddress };
  }, [primaryApplicant]);

  const hasSameMainAddress = Boolean(
    !isPrimaryApplicant &&
    formattedMainAddress &&
    formattedMainAddress === primaryApplicantFormattedAddress.mainAddress,
  );

  const hasSameMailingAddress = Boolean(
    !isPrimaryApplicant &&
    formattedMailingAddress &&
    formattedMailingAddress === primaryApplicantFormattedAddress.mailingAddress,
  );

  const form = useForm<HouseholdMemberAddressFormType>({
    defaultValues: {
      mainAddressStreetOne: member.mainAddressStreetOne,
      mainAddressStreetTwo: member.mainAddressStreetTwo,
      mainAddressComplement: member.mainAddressComplement,
      mainAddressCity: member.mainAddressCity,
      mainAddressState: member.mainAddressState,
      mainAddressZip: member.mainAddressZip,
      mainCounty: member.mainCounty,
      mailingAddressStreetOne: member.mailingAddressStreetOne,
      mailingAddressStreetTwo: member.mailingAddressStreetTwo,
      mailingAddressComplement: member.mailingAddressComplement,
      mailingAddressCity: member.mailingAddressCity,
      mailingAddressState: member.mailingAddressState,
      mailingAddressZip: member.mailingAddressZip,
      mailingCounty: member.mailingCounty,
      isHomeless: member.isHomeless ?? false,
      hasNoMailingAddress: member.hasNoMailingAddress ?? false,
      mainFormattedAddress: formattedMainAddress,
      mailingFormattedAddress: formattedMailingAddress,
    },
    resolver: standardSchemaResolver(householdMemberAddressFormSchema),
  });

  const { setValue, watch, control } = form;
  const [shouldShowEditButton, setShouldShowEditButton] = useState(
    Boolean(formattedMainAddress) ||
      Boolean(formattedMailingAddress) ||
      Boolean(member.isHomeless) ||
      Boolean(member.hasNoMailingAddress),
  );

  const [
    isHomelessState,
    hasNoMailingAddressState,
    mainFormattedAddressState,
    mailingFormattedAddressState,
    mainAddressStreetOneState,
    mailingAddressStreetOneState,
  ] = watch([
    "isHomeless",
    "hasNoMailingAddress",
    "mainFormattedAddress",
    "mailingFormattedAddress",
    "mainAddressStreetOne",
    "mailingAddressStreetOne",
  ]);

  const handleSave = (formData: HouseholdMemberAddressFormType) => {
    const householdMemberData = {
      ...formData,
      ...(formData.isHomeless
        ? {
            mainAddressStreetOne: null,
            mainAddressStreetTwo: null,
            mainAddressComplement: null,
            mainAddressCity: null,
            mainAddressState: null,
            mainAddressZip: null,
            mainCounty: null,
          }
        : {}),
      ...(formData.hasNoMailingAddress
        ? {
            mailingAddressStreetOne: null,
            mailingAddressStreetTwo: null,
            mailingAddressComplement: null,
            mailingAddressCity: null,
            mailingAddressState: null,
            mailingAddressZip: null,
            mailingCounty: null,
          }
        : {}),
    };

    const householdMemberUpdateData = {
      mainAddressStreetOne: householdMemberData.mainAddressStreetOne,
      mainAddressStreetTwo: householdMemberData.mainAddressStreetTwo,
      mainAddressComplement: householdMemberData.mainAddressComplement,
      mainAddressCity: householdMemberData.mainAddressCity,
      mainAddressState: householdMemberData.mainAddressState,
      mainAddressZip: householdMemberData.mainAddressZip,
      mainCounty: householdMemberData.mainCounty,
      mailingAddressStreetOne: householdMemberData.mailingAddressStreetOne,
      mailingAddressStreetTwo: householdMemberData.mailingAddressStreetTwo,
      mailingAddressComplement: householdMemberData.mailingAddressComplement,
      mailingAddressCity: householdMemberData.mailingAddressCity,
      mailingAddressState: householdMemberData.mailingAddressState,
      mailingAddressZip: householdMemberData.mailingAddressZip,
      mailingCounty: householdMemberData.mailingCounty,
      isHomeless: householdMemberData.isHomeless,
      hasNoMailingAddress: householdMemberData.hasNoMailingAddress,
    };

    const onSuccess = () => {
      setShouldShowEditButton(
        Boolean(householdMemberData.mainFormattedAddress) ||
          Boolean(householdMemberData.mailingFormattedAddress) ||
          Boolean(householdMemberData.isHomeless) ||
          Boolean(householdMemberData.hasNoMailingAddress),
      );
      onToggleExpand();
    };

    if (isPrimaryApplicant && onPrimaryApplicantSaveSuccess) {
      onPrimaryApplicantSaveSuccess({
        savedFormData: householdMemberData,
        oldMemberData: member,
        onDone: onSuccess,
      });
      return;
    }

    updateHouseholdMember(
      { id: member.id, householdMemberData: householdMemberUpdateData },
      { onSuccess },
    );
  };
  const onCancel = () => {
    form.reset();
    onClose();
  };

  const onSave = () => {
    void form.handleSubmit(handleSave)();
  };

  useEffect(() => {
    if (isExpanded) {
      form.reset({
        mainAddressStreetOne: member.mainAddressStreetOne,
        mainAddressStreetTwo: member.mainAddressStreetTwo,
        mainAddressComplement: member.mainAddressComplement,
        mainAddressCity: member.mainAddressCity,
        mainAddressState: member.mainAddressState,
        mainAddressZip: member.mainAddressZip,
        mainCounty: member.mainCounty,
        mailingAddressStreetOne: member.mailingAddressStreetOne,
        mailingAddressStreetTwo: member.mailingAddressStreetTwo,
        mailingAddressComplement: member.mailingAddressComplement,
        mailingAddressCity: member.mailingAddressCity,
        mailingAddressState: member.mailingAddressState,
        mailingAddressZip: member.mailingAddressZip,
        mailingCounty: member.mailingCounty,
        isHomeless: member.isHomeless ?? false,
        hasNoMailingAddress: member.hasNoMailingAddress ?? false,
        mainFormattedAddress: formattedMainAddress,
        mailingFormattedAddress: formattedMailingAddress,
      });
    }
  }, [isExpanded, member, formattedMainAddress, formattedMailingAddress, form]);

  useEffect(() => {
    if (mainFormattedAddressState === "") {
      setMainAddressFormFields({ setValue, fields: null });
    }
    if (mailingFormattedAddressState === "") {
      setMailingAddressFormFields({ setValue, fields: null });
    }
  }, [mainFormattedAddressState, mailingFormattedAddressState, setValue]);

  const formattedMemberName = formatHouseholdMemberName(member);

  return (
    <VnForm form={form} isInputOnlyForm>
      <VnExpandableCard
        title={
          isPrimaryApplicant
            ? t("native:applicationAddressScreen.you", {
                name: formattedMemberName,
              })
            : formattedMemberName
        }
        subtitleComponent={
          <AddressSummary
            isHomeless={member.isHomeless ?? false}
            hasNoMailingAddress={member.hasNoMailingAddress ?? false}
            formattedMainAddress={formattedMainAddress}
            formattedMailingAddress={formattedMailingAddress}
            hasSameMainAddress={hasSameMainAddress}
            hasSameMailingAddress={hasSameMailingAddress}
          />
        }
        isExpanded={isExpanded}
        onToggleExpand={onToggleExpand}
        onSave={onSave}
        onCancel={onCancel}
        isLoading={isPending}
        shouldShowEditButton={shouldShowEditButton}>
        {!isHomelessState && (
          <VnAddressInput
            name="mainFormattedAddress"
            control={control}
            labelI18nKey="native:profileInformationScreen.homeAddress"
            ref={mainAddressRef}
            returnKeyType="next"
            onSubmitEditing={() => mailingAddressRef.current?.focus()}
            isAddressSelected={Boolean(mainAddressStreetOneState)}
            onClear={() => {
              setMainAddressFormFields({ setValue, fields: null });
            }}
            onPlaceSelected={(addressComponents, formattedAddress) => {
              const parsed = parseAddressComponents(addressComponents);
              setMainAddressFormFields({
                setValue,
                fields: {
                  mainAddressStreetOne: parsed.streetOne,
                  mainAddressStreetTwo: parsed.streetTwo,
                  mainAddressCity: parsed.city,
                  mainAddressState: parsed.state,
                  mainAddressZip: parsed.zip,
                  mainCounty: parsed.county,
                },
                formattedAddress,
              });
            }}
            onTextCleared={() => {
              setMainAddressFormFields({ setValue, fields: null });
            }}
          />
        )}
        {!isHomelessState && (
          <VnTextInput
            name="mainAddressComplement"
            control={control}
            placeholderI18nKey="native:profileInformationScreen.addressComplement"
            isRequired={false}
          />
        )}
        {!hasNoMailingAddressState && (
          <>
            <VnAddressInput
              name="mailingFormattedAddress"
              control={control}
              labelI18nKey="native:profileInformationScreen.mailingAddress"
              ref={mailingAddressRef}
              returnKeyType="done"
              isAddressSelected={Boolean(mailingAddressStreetOneState)}
              onClear={() => {
                setMailingAddressFormFields({ setValue, fields: null });
              }}
              onPlaceSelected={(addressComponents, formattedAddress) => {
                const parsed = parseAddressComponents(addressComponents);
                setMailingAddressFormFields({
                  setValue,
                  fields: {
                    mailingAddressStreetOne: parsed.streetOne,
                    mailingAddressStreetTwo: parsed.streetTwo,
                    mailingAddressCity: parsed.city,
                    mailingAddressState: parsed.state,
                    mailingAddressZip: parsed.zip,
                    mailingCounty: parsed.county,
                  },
                  formattedAddress,
                });
              }}
              onTextCleared={() => {
                setMailingAddressFormFields({ setValue, fields: null });
              }}
            />
            <VnTextInput
              name="mailingAddressComplement"
              control={control}
              placeholderI18nKey="native:profileInformationScreen.mailingAddressComplement"
              isRequired={false}
            />
          </>
        )}
        <VnCheckboxInput
          name="isHomeless"
          control={control}
          isRequired={false}
          inputClassName="bg-card"
          option={{
            label: t("native:profileInformationScreen.noHomeAddress"),
          }}
        />
        <VnCheckboxInput
          name="hasNoMailingAddress"
          control={control}
          isRequired={false}
          inputClassName="bg-card"
          option={{
            label: t("native:profileInformationScreen.noMailingAddress"),
          }}
        />
      </VnExpandableCard>
    </VnForm>
  );
};
